import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, X, Sparkles, Award, ShieldCheck, MapPin, ArrowRight,
  Phone, MessageCircle, BadgeCheck, Image as ImageIcon, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import {
  GALLERY_VIDEOS, GALLERY_FILTERS, GALLERY_STATS,
} from '../data/galleryData';

const STATS_TILES = [
  { Icon: BadgeCheck, value: GALLERY_STATS.treatments, label: 'Successful Treatments' },
  { Icon: Award, value: GALLERY_STATS.satisfaction, label: 'Customer Satisfaction' },
  { Icon: MapPin, value: GALLERY_STATS.cities, label: 'Cities Served' },
  { Icon: ShieldCheck, value: GALLERY_STATS.experience, label: 'Years Experience' },
];

export default function GalleryVideos({ onOpenCalculator }) {
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  useSeo(
    'Treatment Videos — EntoRid Pest Control',
    'Watch real EntoRid pest control treatment videos — see our certified technicians at work across termite, cockroach, rodent, bed-bug and mosquito services.'
  );

  const items = useMemo(
    () => filter === 'all' ? GALLERY_VIDEOS : GALLERY_VIDEOS.filter((v) => v.service === filter),
    [filter]
  );

  // Lock body scroll while modal is open
  useEffect(() => {
    if (active) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [active]);

  // ESC closes modal
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span>Gallery</span>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Videos</span>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-3 overflow-x-auto" data-testid="gallery-tabs">
            <Link
              to="/gallery/images"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 whitespace-nowrap"
              data-testid="tab-images"
            >
              <ImageIcon size={15} /> Images
            </Link>
            <Link
              to="/gallery/videos"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-sm whitespace-nowrap"
              data-testid="tab-videos-active"
            >
              <Video size={15} /> Videos
            </Link>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900 text-white">
        <div aria-hidden className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1 text-[11px] sm:text-xs font-semibold">
            <Sparkles size={13} className="text-emerald-300" /> Behind the Scenes · Real Treatments
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl" data-testid="gallery-videos-title">
            See Our Technicians at Work
          </h1>
          <p className="mt-3 text-sm sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            Watch real treatment videos from our certified pest-control specialists across Uttarakhand.
          </p>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {STATS_TILES.map(({ Icon, value, label }) => (
              <div key={label} className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-3 sm:px-4 sm:py-4 flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-base sm:text-xl font-extrabold leading-none">{value}</div>
                  <div className="text-[10px] sm:text-xs text-white/65 mt-1 leading-tight">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth no-scrollbar"
            data-testid="gallery-video-filters"
          >
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`vfilter-${f.id}`}
                className={`flex-shrink-0 px-4 h-10 min-h-[44px] rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  filter === f.id
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {items.length === 0 ? (
          <div className="text-center py-16 sm:py-24" data-testid="videos-empty">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-700 mb-4">
              <Video size={26} />
            </div>
            <h3 className="text-xl font-semibold text-slate-900">More Treatment Videos Coming Soon</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
              We&apos;re recording fresh on-site treatment videos in this category. Switch to{' '}
              <button onClick={() => setFilter('all')} className="text-emerald-700 font-semibold underline" data-testid="videos-empty-all">All Videos</button>{' '}
              or check back next week.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="video-grid">
            {items.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setActive(v)}
                data-testid={`video-card-${v.id}`}
                className="group text-left bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden"
              >
                <div className="relative aspect-video bg-black overflow-hidden">
                  {v.mp4Url ? (
                    <video
                      src={`${v.mp4Url}#t=0.5`}
                      muted
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-contain bg-black group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <img
                      src={v.thumbnail}
                      alt={v.title}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/95 group-hover:bg-emerald-500 group-hover:text-white text-emerald-600 flex items-center justify-center shadow-2xl transition-colors duration-300">
                      <Play size={26} className="ml-1" />
                    </span>
                  </div>
                  <span className="absolute bottom-3 right-3 bg-black/70 text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded">
                    {v.duration}
                  </span>
                  <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${chipClass(v.service)}`}>
                    {labelOf(v.service)}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug line-clamp-2">{v.title}</h3>
                  <p className="text-xs sm:text-[13px] text-gray-600 leading-relaxed mt-1.5 line-clamp-2">{v.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* CTA */}
        <section className="mt-12 sm:mt-16 rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700 text-white p-6 sm:p-10 lg:p-14 relative overflow-hidden" data-testid="video-cta">
          <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight max-w-2xl">
              Get the Same Results for Your Home
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/85 max-w-2xl leading-relaxed">
              Same-day appointments · Government-approved chemicals · Free re-treatment warranty.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onOpenCalculator}
                data-testid="video-book-now"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-bold shadow-lg"
              >
                Book Now <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Button
                onClick={onOpenCalculator}
                data-testid="video-free-inspection"
                className="h-12 min-h-[48px] px-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold shadow-lg"
              >
                Get Free Inspection
              </Button>
              <a
                href="https://api.whatsapp.com/send?phone=919410110910"
                target="_blank"
                rel="noreferrer"
                data-testid="video-whatsapp"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2 font-bold transition"
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* MODAL */}
      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
          onClick={() => setActive(null)}
          data-testid="video-modal"
        >
          <div
            className="bg-black w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] sm:rounded-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-black border-b border-white/10">
              <div className="min-w-0 pr-3 text-white">
                <h3 className="text-sm sm:text-base font-bold truncate">{active.title}</h3>
                <p className="text-[11px] sm:text-xs text-white/60 truncate">{active.duration} · {labelOf(active.service)}</p>
              </div>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="video-modal-close"
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 transition flex items-center justify-center text-white flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
            {/* Player */}
            <div className="bg-black w-full flex items-center justify-center" style={{ height: '55vh', minHeight: '340px' }}>
              {active.mp4Url ? (
                <video
                  src={active.mp4Url}
                  controls
                  autoPlay
                  playsInline
                  className="h-full w-auto max-w-full object-contain bg-black"
                  data-testid="video-mp4-player"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <iframe
                  src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=1`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full"
                  style={{ aspectRatio: '16/9', maxWidth: '100%' }}
                  data-testid="video-iframe"
                />
              )}
            </div>
            {/* Caption + CTA */}
            <div className="bg-white px-4 py-4 sm:px-6 sm:py-5 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <p className="text-sm text-gray-700 leading-relaxed">{active.description}</p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => { setActive(null); onOpenCalculator?.(); }}
                  data-testid="video-modal-book"
                  className="flex-1 h-12 min-h-[48px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  Book This Service <ArrowRight size={16} className="ml-1.5" />
                </Button>
                <a
                  href="tel:+917819988010"
                  data-testid="video-modal-call"
                  className="flex-shrink-0 h-12 min-h-[48px] px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold inline-flex items-center justify-center gap-2"
                >
                  <Phone size={16} /> Call
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function labelOf(service) {
  const map = {
    termite: 'Termite',
    cockroach: 'Cockroach',
    bedbug: 'Bed Bugs',
    rodent: 'Rodent',
    mosquito: 'Mosquito',
  };
  return map[service] || service;
}

function chipClass(service) {
  const map = {
    termite: 'bg-amber-500/95 text-white',
    cockroach: 'bg-emerald-600/95 text-white',
    bedbug: 'bg-rose-600/95 text-white',
    rodent: 'bg-slate-700/95 text-white',
    mosquito: 'bg-sky-600/95 text-white',
  };
  return map[service] || 'bg-slate-700/95 text-white';
}
